import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShieldCheck, RefreshCcw, Truck, BadgeCheck } from "lucide-react";
import api from "@/api";
import { useStore } from "@/store/StoreContext";
import Seo from "@/components/store/Seo";
import ProductCarousel from "@/components/store/ProductCarousel";
import FootwearLoader from "@/components/store/FootwearLoader";
import VideoSection from "@/components/store/VideoSection";
import { applyHomeAnimations, getAnimConfig } from "@/anim/engine";
import { Sparkles, Crown, Gem, Award, Leaf, Footprints, Scissors, Package, Lock, Star, Heart as HeartIcon } from "lucide-react";
import { imgW, srcSet, imgFallback } from "@/utils/img";

function Hero({ banners }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length]);
  if (!banners.length) return null;
  const b = banners[idx];
  return (
    <section className="relative bg-[#111111]" data-testid="hero-slider" data-section="hero">
      {banners.map((bn, i) => (
        <div key={bn.id} aria-hidden={i !== idx}
          className={`transition-opacity duration-1000 ${i === idx ? "relative opacity-100" : "absolute inset-0 opacity-0 pointer-events-none overflow-hidden"}`}>
          {bn.video ? (
            <video src={bn.video} poster={bn.image_desktop} autoPlay muted loop playsInline data-testid="hero-video" className="w-full h-auto block" />
          ) : (
          <picture>
            <source media="(max-width: 640px)" srcSet={bn.image_mobile || bn.image_desktop} />
            <img src={imgW(bn.image_desktop, 1600)} srcSet={srcSet(bn.image_desktop)} sizes="100vw" alt={bn.title} className="w-full h-auto block tq-kenburns" loading={i === 0 ? "eager" : "lazy"} fetchPriority={i === 0 ? "high" : "auto"} onError={imgFallback} />
          </picture>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/70 via-[#111111]/15 to-transparent" />
        </div>
      ))}
      <div className="absolute inset-0 flex items-end">
        <div className="tq-container pb-20 sm:pb-28 w-full">
          <p className="tq-overline !text-[#C59B27] mb-3 tq-fade-up" key={`tag-${idx}`}>{b.tag}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white max-w-2xl leading-[1.05] tq-fade-up" key={`title-${idx}`} style={{ animationDelay: "100ms" }}>{b.title}</h1>
          {b.subtitle && <p className="hidden sm:block text-white/80 mt-4 max-w-xl text-sm sm:text-base tq-fade-up" key={`sub-${idx}`} style={{ animationDelay: "200ms" }}>{b.subtitle}</p>}
          {b.button_text && (
            <Link to={b.button_link || "/collections/new-arrivals"} data-testid="hero-cta"
              className="tq-btn-gold mt-7 inline-flex tq-fade-up" style={{ animationDelay: "300ms" }}>
              {b.button_text}
            </Link>
          )}
          {b.button2_text && (
            <Link to={b.button2_link || "/collections/best-sellers"} data-testid="hero-cta-2"
              className="tq-btn mt-7 sm:ml-3 inline-flex border border-white/70 text-white hover:bg-white hover:text-[#111111] tq-fade-up" style={{ animationDelay: "380ms" }}>
              {b.button2_text}
            </Link>
          )}
        </div>
      </div>
      {banners.length > 1 && (
        <div className="absolute bottom-6 right-4 sm:right-8 flex gap-2">
          <button onClick={() => setIdx((idx - 1 + banners.length) % banners.length)} aria-label="Previous slide" data-testid="hero-carousel-prev"
            className="w-10 h-10 border border-white/40 text-white flex items-center justify-center hover:bg-[#C59B27] hover:border-[#C59B27] hover:text-[#111111] transition-colors"><ChevronLeft size={18} /></button>
          <button onClick={() => setIdx((idx + 1) % banners.length)} aria-label="Next slide" data-testid="hero-carousel-next"
            className="w-10 h-10 border border-white/40 text-white flex items-center justify-center hover:bg-[#C59B27] hover:border-[#C59B27] hover:text-[#111111] transition-colors"><ChevronRight size={18} /></button>
        </div>
      )}
    </section>
  );
}

function CategoryGrid({ section }) {
  const cats = section.categories || [];
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  if (!cats.length) return null;
  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const step = card ? card.offsetWidth + 16 : el.clientWidth * 0.7;
    el.scrollBy({ left: dir * step * 2, behavior: "smooth" });
  };
  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };
  return (
    <section className="py-20 sm:py-28 bg-white" data-section="shop_by_style">
      <div className="tq-container">
        <div className="text-center mb-10">
          <p className="tq-overline mb-2">{section.config?.overline || "The Collection"}</p>
          <h2 className="font-serif text-3xl sm:text-4xl">{section.title}</h2>
          {section.subtitle && <p className="text-sm text-[#666666] mt-2">{section.subtitle}</p>}
        </div>
        <div className="relative group/carousel" data-testid="category-carousel">
          <button onClick={() => scrollBy(-1)} disabled={!canPrev} aria-label="Previous categories" data-testid="category-carousel-prev"
            className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white border border-[#EAEAEA] shadow-lg items-center justify-center rounded-full hover:bg-[#111111] hover:text-[#C59B27] transition-colors disabled:opacity-30 disabled:pointer-events-none">
            <ChevronLeft size={18} />
          </button>
          <div ref={trackRef} onScroll={onScroll}
            className="flex gap-3 sm:gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth pb-1"
            style={{ WebkitOverflowScrolling: "touch" }}>
            {cats.map((c) => (
              <Link key={c.id} to={`/collections/${c.slug}`} data-card data-testid={`category-card-${c.slug}`}
                className="group relative overflow-hidden shrink-0 snap-start w-[46%] sm:w-[30%] lg:w-[22%] xl:w-[18%] aspect-[3/4] bg-[#FAFAFA]">
                {c.image && <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white font-serif text-xl">{c.name}</p>
                  <p className="text-white/70 text-[10px] uppercase tracking-[0.25em] mt-1 group-hover:text-[#C59B27] transition-colors">{section.config?.cta_text || "Shop Now"} →</p>
                </div>
              </Link>
            ))}
          </div>
          <button onClick={() => scrollBy(1)} disabled={!canNext} aria-label="Next categories" data-testid="category-carousel-next"
            className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white border border-[#EAEAEA] shadow-lg items-center justify-center rounded-full hover:bg-[#111111] hover:text-[#C59B27] transition-colors disabled:opacity-30 disabled:pointer-events-none">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

function BannerStrip({ banner }) {
  if (!banner) return null;
  return (
    <section className="relative" data-section="mid_banner">
      {banner.video ? (
        <video src={banner.video} poster={banner.image_desktop} autoPlay muted loop playsInline data-testid="banner-strip-video" className="w-full h-auto block" />
      ) : (
      <picture>
        <source media="(max-width: 640px)" srcSet={banner.image_mobile || banner.image_desktop} />
        <img src={banner.image_desktop} alt={banner.title} loading="lazy" className="w-full h-auto block" />
      </picture>
      )}
      <div className="absolute inset-0 bg-[#111111]/55" />
      <div className="absolute inset-0 tq-container flex flex-col items-center justify-center text-center px-4 py-6">
        <p className="tq-overline !text-[#C59B27] mb-3">{banner.tag}</p>
        <h2 className="font-serif text-3xl sm:text-5xl text-white max-w-2xl">{banner.title}</h2>
        {banner.subtitle && <p className="text-white/80 mt-3">{banner.subtitle}</p>}
        {banner.button_text && <Link to={banner.button_link || "/collections/sale"} data-testid="banner-strip-cta" className="tq-btn-gold mt-6">{banner.button_text}</Link>}
      </div>
    </section>
  );
}

const TRUST = [
  { icon: "ShieldCheck", title: "Secure Payments", sub: "Razorpay & COD available" },
  { icon: "RefreshCcw", title: "7-Day Exchange", sub: "Easy size exchanges" },
  { icon: "Truck", title: "Pan-India Delivery", sub: "Free shipping above ₹1,999" },
  { icon: "BadgeCheck", title: "Handcrafted Quality", sub: "Made by Indian artisans" },
];

const TRUST_ICONS = { ShieldCheck, RefreshCcw, Truck, BadgeCheck, Sparkles, Crown, Gem, Award, Leaf, Footprints, Scissors, Package, Lock, Star, Heart: HeartIcon };

function Trust({ section }) {
  const badges = (section?.config?.badges?.length ? section.config.badges : TRUST).filter((b) => b.enabled !== false);
  if (!badges.length) return null;
  return (
    <section className="py-16 sm:py-24 bg-[#FAFAFA]" data-section="trust">
      <div className="tq-container grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
        {badges.map((t, i) => {
          const IconC = TRUST_ICONS[t.icon];
          const vis = `${t.desktop === false ? "hidden md:block" : ""} ${t.mobile === false ? "md:hidden" : ""}`;
          return (
            <div key={i} className={`text-center group ${vis}`} data-testid={`trust-badge-${i}`}>
              {t.icon && (t.icon.startsWith("/") || t.icon.startsWith("http"))
                ? <img src={t.icon} alt="" loading="lazy" className="mx-auto h-7 w-7 object-contain transition-transform duration-300 group-hover:scale-110" />
                : IconC ? <IconC size={26} strokeWidth={1.2} className="mx-auto text-[#111111] transition-transform duration-300 group-hover:scale-110" /> : null}
              <p className="font-serif text-lg mt-3">{t.title}</p>
              <p className="text-xs text-[#666666] mt-1">{t.sub}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ReviewsSection({ section }) {
  const reviews = section?.reviews || [];
  const c = section?.config || {};
  const count = c.count || 4;
  if (!reviews.length) return null;
  const cols = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[c.columns || 4];
  return (
    <section className="py-20 sm:py-28" data-section="reviews">
      <div className="tq-container">
        <div className="text-center mb-10">
          <p className="tq-overline mb-2">{c.overline || "Verified Reviews"}</p>
          <h2 className="font-serif text-3xl sm:text-4xl">{section.title || "From Our Customers"}</h2>
          {section.subtitle && <p className="text-sm text-[#666666] mt-2">{section.subtitle}</p>}
          <p className="text-xs text-[#8A6A12] mt-3" data-testid="reviews-stats">
            {(reviews.reduce((a, r) => a + (r.rating || 0), 0) / reviews.length).toFixed(1)}/5 from our TARISHQ community · {reviews.length} verified reviews
          </p>
        </div>
        <div className={`grid ${cols} gap-5`}>
          {reviews.slice(0, count).map((r) => (
            <div key={r.id} className="tq-card p-6">
              <p className="text-[#C59B27] tracking-widest">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</p>
              <p className="text-sm text-[#333] mt-3 leading-relaxed line-clamp-4">"{r.body}"</p>
              <p className="text-xs font-semibold uppercase tracking-wider mt-4">{r.name}{r.verified_purchase && <span className="text-[#1A3C34] ml-2 normal-case">· Verified Buyer</span>}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection({ section, faqs }) {
  const [open, setOpen] = useState(null);
  const c = section?.config || {};
  const items = (c.items?.length ? c.items : (faqs || [])).filter((f) => f.question);
  if (!items.length) return null;
  return (
    <section className="py-20 sm:py-28 bg-white" data-section="faq">
      <div className="tq-container max-w-3xl">
        <div className="text-center mb-10">
          <p className="tq-overline mb-2">{c.overline || "Good to Know"}</p>
          <h2 className="font-serif text-3xl sm:text-4xl">{section.title || "Questions, Answered"}</h2>
          {section.subtitle && <p className="text-sm text-[#666666] mt-2">{section.subtitle}</p>}
        </div>
        <div className="divide-y divide-[#EAEAEA] border-y border-[#EAEAEA]">
          {items.map((f, i) => (
            <div key={f.id || i}>
              <button onClick={() => setOpen(open === i ? null : i)} data-testid={`faq-toggle-${i}`}
                className="w-full flex items-center justify-between py-5 text-left font-medium text-[#111111] hover:text-[#A8821B] transition-colors">
                {f.question}
                <span className={`ml-4 transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              <div className={`tq-acc ${open === i ? "open" : ""}`}><div><p className="pb-5 text-sm text-[#666666] leading-relaxed">{f.answer}</p></div></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const GRID_COLS = { 2: "grid-cols-2", 3: "grid-cols-2 sm:grid-cols-3", 4: "grid-cols-2 lg:grid-cols-4", 5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5", 6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" };

function TrustMarquee({ section }) {
  const c = section?.config || {};
  const items = (c.items || []).filter((i) => i.enabled !== false && i.text);
  if (!items.length) return null;
  const content = items.map((a, i) => (
    <span key={i} className="flex items-center text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-[#666666]">
      {a.link
        ? <Link to={a.link} className="hover:text-[#A8821B] transition-colors px-8 whitespace-nowrap">{a.text}</Link>
        : <span className="px-8 whitespace-nowrap">{a.text}</span>}
      <span className="text-[#C59B27]" aria-hidden="true">✦</span>
    </span>
  ));
  return (
    <section className="border-y border-[#EAEAEA] bg-white py-3.5 overflow-hidden" data-section="trust_marquee" data-testid="trust-marquee">
      {c.scroll === false ? (
        <div className="flex flex-wrap justify-center">{content}</div>
      ) : (
        <div className="tq-marquee">
          <div className="tq-marquee-track" style={{ "--tq-marquee-dur": `${c.speed || 35}s` }}>
            {[0, 1].map((d) => <div key={d} aria-hidden={d === 1} className="flex items-center shrink-0">{content}</div>)}
          </div>
        </div>
      )}
    </section>
  );
}

function WhyUs({ section }) {
  const c = section?.config || {};
  const items = (c.items || []).filter((i) => i.enabled !== false && i.title);
  if (!items.length) return null;
  return (
    <section className="py-20 sm:py-28 bg-[#111111] text-white" data-section="why_us" data-testid="why-us">
      <div className="tq-container">
        <div className="max-w-2xl mb-12">
          {section.subtitle && <p className="tq-overline !text-[#C59B27] mb-3">{section.subtitle}</p>}
          <h2 className="font-serif text-3xl sm:text-5xl leading-tight">{section.title}</h2>
          {c.text && <p className="text-white/60 mt-4 text-sm sm:text-base leading-relaxed">{c.text}</p>}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {items.map((it, i) => (
            <div key={i} className="border-t border-white/15 pt-5" data-testid={`why-us-item-${i}`}>
              <p className="text-[#C59B27] text-xs tracking-[0.3em] mb-3">{String(i + 1).padStart(2, "0")}</p>
              <p className="font-serif text-xl mb-2">{it.title}</p>
              {it.text && <p className="text-white/55 text-sm leading-relaxed">{it.text}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImageGrid({ section }) {
  const items = (section?.config?.items || []).filter((i) => (i.image || i.title) && i.enabled !== false);
  if (!items.length) return null;
  const cols = GRID_COLS[section.config?.columns] || (items.length <= 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 lg:grid-cols-4");
  return (
    <section className="py-20 sm:py-28 bg-white" data-section={section.id}>
      <div className="tq-container">
        <div className="text-center mb-10">
          {section.subtitle && <p className="tq-overline mb-2">{section.subtitle}</p>}
          <h2 className="font-serif text-3xl sm:text-4xl">{section.title}</h2>
        </div>
        <div className={`grid gap-3 sm:gap-5 ${cols}`}>
          {items.map((it, i) => (
            <Link key={i} to={it.link || "/collections/new-arrivals"} className="group relative overflow-hidden bg-[#F5F5F5]" data-testid={`image-grid-${i}`}>
              <div className="aspect-[4/5] overflow-hidden">
                {it.image && <img src={imgW(it.image, 700)} srcSet={srcSet(it.image)} sizes="(max-width:640px) 50vw, 25vw" alt={it.title} loading="lazy" onError={imgFallback} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-4 sm:p-5">
                <p className="font-serif text-lg sm:text-xl text-white">{it.title}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/70 mt-1 group-hover:text-[#C59B27] transition-colors">Shop Now →</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function BrandStory({ section }) {
  const c = section?.config || {};
  const flip = c.layout === "image_right";
  return (
    <section className="py-20 sm:py-28" data-section="brand_story">
      <div className="tq-container grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className={`overflow-hidden bg-[#F5F5F5] order-2 ${flip ? "lg:order-2" : "lg:order-1"}`}>
          {c.image && <img src={imgW(c.image, 900)} srcSet={srcSet(c.image)} sizes="(max-width:1024px) 100vw, 50vw" alt={section.title} loading="lazy" onError={imgFallback} className="w-full h-auto block" />}
        </div>
        <div className={`order-1 ${flip ? "lg:order-1" : "lg:order-2"}`}>
          {section.subtitle && <p className="tq-overline mb-3">{section.subtitle}</p>}
          <h2 className="font-serif text-3xl sm:text-4xl mb-5">{section.title}</h2>
          <p className="text-[#666666] leading-relaxed whitespace-pre-line">{c.text}</p>
          {c.button_text && <Link to={c.link || "/our-story"} data-testid="brand-story-cta" className="tq-btn-outline mt-7">{c.button_text}</Link>}
        </div>
      </div>
    </section>
  );
}

function Newsletter({ section }) {
  const c = section?.config || {};
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try { await api.post("/newsletter", { email }); } catch { /* still confirm */ }
    setDone(true);
    setBusy(false);
  };
  return (
    <section className="py-20 sm:py-28 bg-[#111111]" data-section="newsletter" data-testid="newsletter-section">
      <div className="tq-container max-w-2xl text-center">
        {section.subtitle && <p className="tq-overline !text-[#C59B27] mb-3">{section.subtitle}</p>}
        <h2 className="font-serif text-3xl sm:text-4xl text-white">{section.title}</h2>
        {c.text && <p className="text-white/70 text-sm mt-3">{c.text}</p>}
        {done ? (
          <p className="text-[#C59B27] mt-7 text-sm tracking-wide" data-testid="newsletter-success">{c.success_message || "You're in! Watch your inbox."}</p>
        ) : (
          <form onSubmit={submit} className="mt-7 flex max-w-md mx-auto">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={c.placeholder || "Your email address"} data-testid="newsletter-email"
              className="flex-1 bg-white/10 border border-white/20 text-white placeholder-white/40 px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
            <button disabled={busy} data-testid="newsletter-submit" className="tq-btn-gold !py-3">{busy ? "…" : (c.button_text || "Subscribe")}</button>
          </form>
        )}
      </div>
    </section>
  );
}

function RecentlyViewed({ title, subtitle }) {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    let slugs = [];
    try { slugs = JSON.parse(localStorage.getItem("tq_recent") || "[]"); } catch { /* ignore */ }
    if (!slugs.length) { setProducts([]); return; }
    api.get("/products", { params: { slugs: slugs.join(","), limit: 12 } })
      .then(({ data }) => {
        const order = new Map(slugs.map((s, i) => [s, i]));
        setProducts((data.products || []).sort((a, b) => order.get(a.slug) - order.get(b.slug)));
      })
      .catch(() => setProducts([]));
  }, []);
  if (!products || !products.length) return null;
  return <ProductCarousel title={title || "Recently Viewed"} subtitle={subtitle} products={products} sectionKey="recently_viewed" />;
}

export default function Home() {
  const [data, setData] = useState(null);
  const { settings } = useStore();
  useEffect(() => { api.get("/home").then(({ data }) => setData(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!data) return;
    return applyHomeAnimations(getAnimConfig(settings));
  }, [data, settings]);
  const mii = settings?.made_in_india || {};
  return (
    <div data-testid="home-page">
      <Seo title="" />
      {!data ? (
        <FootwearLoader />
      ) : (
        <>
          {(data.sections || []).some((s) => s.type === "hero") && <Hero banners={data.hero_banners || []} />}
          {mii.enabled && mii.show_home !== false && (
            <div className="bg-[#111111] py-3 text-center">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#C59B27]" data-testid="made-in-india-home">{mii.text || "Proudly Made in India"} · Handcrafted by Indian Artisans</p>
            </div>
          )}
          {(data.sections || []).map((s) => {
            if (s.type === "hero") return null;
            if (s.type === "category_grid") return <CategoryGrid key={s.id} section={s} />;
            if (s.type === "product_carousel") return <ProductCarousel key={s.id} sectionKey={(s.config?.link || "").split("/").filter(Boolean).pop() || s.id} title={s.title} subtitle={s.subtitle} products={s.products} link={s.config?.link} />;
            if (s.type === "banner_strip") return <BannerStrip key={s.id} banner={s.banner} />;
            if (s.type === "trust") return <Trust key={s.id} section={s} />;
            if (s.type === "campaign_banner") return <BannerStrip key={s.id} banner={{ ...s.config, title: s.title || s.config?.title, subtitle: s.subtitle || s.config?.subtitle, tag: s.config?.tag || s.subtitle, image_desktop: s.config?.image, image_mobile: s.config?.mobile_image, button_link: s.config?.link }} />;
            if (s.type === "image_grid") return <ImageGrid key={s.id} section={s} />;
            if (s.type === "brand_story") return <BrandStory key={s.id} section={s} />;
            if (s.type === "recently_viewed") return <RecentlyViewed key={s.id} title={s.title} subtitle={s.subtitle} />;
            if (s.type === "reviews") return <ReviewsSection key={s.id} section={s} />;
            if (s.type === "faq") return <FaqSection key={s.id} section={s} faqs={data.faqs} />;
            if (s.type === "trust_marquee") return <TrustMarquee key={s.id} section={s} />;
            if (s.type === "why_us") return <WhyUs key={s.id} section={s} />;
            if (s.type === "newsletter") return <Newsletter key={s.id} section={s} />;
            if (s.type === "videos") return <VideoSection key={s.id} placement={s.config?.placement || "home"} title={s.title || undefined} subtitle={s.subtitle || undefined} autoplay={s.config?.autoplay !== false} />;
            return null;
          })}
          {!(data.sections || []).some((s) => s.type === "videos") && <VideoSection placement="home" />}
        </>
      )}
    </div>
  );
}
